import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import {
  DMOInvitation,
  DMOInvitationListQueryParams,
  DmoInvitationStatus,
} from '@/types/dmo-management.types';
import {
  useGetDMOInvitationsQuery,
  useCancelDMOInvitationMutation,
} from '@/services/DMOManagementService';
import { ColumnDef } from '@tanstack/react-table';
import { formatDisplayDateTime } from '@/utils/Date';

interface DMOInvitationsListProps {
  refreshKey?: number;
}

const DMOInvitationsList = ({ refreshKey }: DMOInvitationsListProps) => {
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const { enqueueSnackbar } = useSnackbar();
  const [dataKey, setDataKey] = useState(() => Date.now());
  const [selectedInvitation, setSelectedInvitation] =
    useState<DMOInvitation | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [confirmCancelState, setConfirmCancelState] = useState<{
    isOpen: boolean;
    invitationId: string | null;
  }>({ isOpen: false, invitationId: null });

  const [cancelInvitation, { isLoading: isCancelling }] =
    useCancelDMOInvitationMutation();

  const queryParams: DMOInvitationListQueryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetDMOInvitationsQuery(queryParams);

  useEffect(() => {
    if (refreshKey !== undefined) {
      refetch();
      setDataKey(Date.now());
    }
  }, [refreshKey, refetch]);

  useEffect(() => {
    if (data) {
      const paginated = data as PaginatedApiResponse<DMOInvitation[]>;
      handleTotalPages(paginated.totalPages || 1);
      handleTotalItems(paginated.totalItems || paginated.data?.length || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  const safeStatusLabel = (value: DmoInvitationStatus | undefined): string => {
    if (!value) return '-';

    switch (value) {
      case DmoInvitationStatus.ACTIVE:
        return 'Active';
      case DmoInvitationStatus.EXPIRED:
        return 'Expired';
      case DmoInvitationStatus.CANCELLED:
        return 'Cancelled';
      case DmoInvitationStatus.COMPLETED:
        return 'Completed';
      default:
        return value;
    }
  };

  const getInvitationStatusBadge = (
    status: DmoInvitationStatus | undefined
  ) => {
    const label = safeStatusLabel(status);

    switch (status) {
      case DmoInvitationStatus.ACTIVE:
        return <span className="badge badge-success">{label}</span>;
      case DmoInvitationStatus.EXPIRED:
        return <span className="badge badge-secondary">{label}</span>;
      case DmoInvitationStatus.CANCELLED:
        return <span className="badge badge-danger">{label}</span>;
      case DmoInvitationStatus.COMPLETED:
        return <span className="badge badge-primary">{label}</span>;
      default:
        return <span className="badge badge-secondary">{label}</span>;
    }
  };

  const formatDateTime = (value: unknown): string => {
    if (!value) return '-';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return String(value);
    return formatDisplayDateTime(date);
  };

  const handleOpenView = useCallback((invitation: DMOInvitation) => {
    setSelectedInvitation(invitation);
    setIsViewModalOpen(true);
  }, []);

  const handleCloseView = () => {
    setIsViewModalOpen(false);
    setSelectedInvitation(null);
  };

  const handleConfirmCancel = async () => {
    if (!confirmCancelState.invitationId) return;
    try {
      await cancelInvitation(confirmCancelState.invitationId).unwrap();
      enqueueSnackbar('Invitation cancelled successfully', {
        variant: 'success',
      });
      setConfirmCancelState({ isOpen: false, invitationId: null });
      refetch();
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        (err as ErrorApiResponse)?.message ||
        'Failed to cancel invitation';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleCancelClick = (invitationId: string) => {
    setConfirmCancelState({ isOpen: true, invitationId });
  };

  const handleCloseCancelDialog = () => {
    setConfirmCancelState({ isOpen: false, invitationId: null });
  };

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setDataKey(Date.now());
    }
  };

  const columns: ColumnDef<DMOInvitation>[] = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-900">{row.original.email}</span>
        ),
        meta: { className: 'min-w-[220px]' },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => getInvitationStatusBadge(row.original.status),
        meta: { className: 'min-w-[140px]' },
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires At',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">
            {formatDateTime(row.original.expires_at)}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {hasPermission('dmo_invitation_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenView(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View details"
                aria-label={`View invitation for ${row.original.email}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {row.original.status === DmoInvitationStatus.ACTIVE &&
              hasPermission('dmo_invitation_management_update') && (
                <button
                  type="button"
                  onClick={() => handleCancelClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Cancel invitation"
                  aria-label={`Cancel invitation for ${row.original.email}`}
                  disabled={isCancelling}
                >
                  <KeenIcon
                    type="filled"
                    icon="cross"
                    className="text-red-500"
                  />
                </button>
              )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenView, hasPermission, isCancelling]
  );

  const invitations = (
    data as PaginatedApiResponse<DMOInvitation[]> | undefined
  )?.data;

  if (!hasPermission('dmo_invitation_management_read')) {
    return (
      <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
        <div className="text-center py-4 text-gray-500" role="alert">
          You don't have permission to view DMO invitations.
        </div>
      </div>
    );
  }

  return (
    <div className="card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
        <div className="flex items-center gap-2">
          <label
            className="input input-sm h-9 flex items-center"
            htmlFor="search-dmo-invitations"
            aria-label="Search invitations by email"
          >
            <KeenIcon
              type="filled"
              icon="magnifier"
              className="text-gray-500"
            />
            <input
              id="search-dmo-invitations"
              placeholder="Search by email"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full sm:w-56 h-9 text-sm"
              aria-label="Search invitations by email"
            />
          </label>
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={resetFilters}
            >
              Reset
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isError ? (
          <div className="text-center py-4 text-red-500">
            Failed to load invitations. Please try again.
          </div>
        ) : (
          <DataGrid
            key={dataKey}
            columns={columns}
            data={invitations || []}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm}
            aria-label="DMO invitations table"
          />
        )}
      </div>

      <Modal
        open={isViewModalOpen}
        onClose={handleCloseView}
        className="!flex items-center justify-center"
      >
        <div className="modal-content w-full max-w-md mx-4">
          <div className="modal-header">
            <h1 className="modal-title">Invitation Details</h1>
            <button
              type="button"
              onClick={handleCloseView}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="px-6 py-4">
            {selectedInvitation ? (
              <div className="space-y-2 text-sm text-gray-800">
                <div className="flex justify-between gap-4">
                  <span className="font-medium">Email</span>
                  <span>{selectedInvitation.email}</span>
                </div>
                {selectedInvitation.status !== undefined && (
                  <div className="flex justify-between gap-4">
                    <span className="font-medium">Status</span>
                    <span>{safeStatusLabel(selectedInvitation.status)}</span>
                  </div>
                )}
                {selectedInvitation.created_at && (
                  <div className="flex justify-between gap-4">
                    <span className="font-medium">Created At</span>
                    <span>{formatDateTime(selectedInvitation.created_at)}</span>
                  </div>
                )}
                {selectedInvitation.expires_at && (
                  <div className="flex justify-between gap-4">
                    <span className="font-medium">Expires At</span>
                    <span>{formatDateTime(selectedInvitation.expires_at)}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-600">No invitation selected.</div>
            )}
          </div>

          <div className="px-6 py-4 flex justify-end gap-2 border-t border-gray-200">
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={handleCloseView}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmCancelState.isOpen}
        onClose={handleCloseCancelDialog}
        className="!flex items-center justify-center"
      >
        <div className="bg-white dark:bg-black rounded-lg shadow-xl w-full max-w-md mx-4 contrast:bg-black contrast:border-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 contrast:border-gray-400 contrast:dark:border-gray-600">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white contrast:text-white">
              Cancel Invitation
            </h1>
            <button
              type="button"
              onClick={handleCloseCancelDialog}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 contrast:text-gray-600 contrast:hover:text-black contrast:dark:text-gray-300 contrast:dark:hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="px-6 py-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 contrast:text-gray-900 contrast:dark:text-white">
              Are you sure you want to cancel this invitation? The invite link
              will no longer be valid.
            </p>
          </div>

          <div className="px-6 py-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-800 contrast:border-gray-400 contrast:dark:border-gray-600">
            <button
              type="button"
              className="btn btn-light btn-sm dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700 contrast:bg-white contrast:text-black contrast:border-black contrast:hover:bg-gray-800"
              onClick={handleCloseCancelDialog}
            >
              Keep Invitation
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm dark:bg-red-800 dark:text-white dark:hover:bg-red-700 contrast:bg-red-600 contrast:text-black contrast:hover:bg-red-700 contrast:dark:bg-red-900 contrast:dark:text-white contrast:dark:hover:bg-red-800 disabled:opacity-50"
              onClick={handleConfirmCancel}
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling...' : 'Cancel Invitation'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export { DMOInvitationsList };
